import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'

import { ApiError, PaginatedApiResult } from '@mangovoice/types'

interface Schedule {
  id: number
  uuid?: string | null
  name: string
  pbx_id: number
  created_at?: string
  updated_at?: string
}

/**
 * Fetches all schedules for a PBX that can be used in switchboard call flow nodes.
 * Schedules are used in schedule nodes to route calls based on time-of-day rules.
 */
export const useGetSchedules = (pbxId: number | null) => {
  const api = useMangoApiClient()

  const { data: schedulesResponse, isLoading } = useQuery<
    PaginatedApiResult<Schedule> | null,
    ApiError
  >({
    queryKey: ['schedules', pbxId],
    queryFn: async () => {
      if (!pbxId) return null

      const response = await api.switchboard.listSchedules({
        pbx_id: pbxId,
        limit: 300,
      })

      if (response.ok && response.data) {
        return response.data
      }

      return null
    },
    enabled: !!pbxId,
    retry: false,
  })

  const schedules = useMemo(() => {
    return (
      schedulesResponse?.results?.map((schedule) => ({
        id: String(schedule.id),
        uuid: schedule.uuid ?? undefined,
        name: schedule.name || `Schedule ${schedule.id}`,
      })) || []
    )
  }, [schedulesResponse])

  return {
    schedules,
    isLoading,
  }
}
